<template>
  <div class="image-control-panel">
    <button
      class="control-btn nav-btn"
      @click="$emit('previous')"
      :disabled="!hasPrevious"
      title="Previous study"
    >
      <font-awesome-icon :icon="['fas', 'chevron-left']" />
    </button>

    <span class="image-counter" v-if="totalImages > 0">
      {{ currentImageIndex + 1 }} / {{ totalImages }}
    </span>

    <button
      class="control-btn nav-btn"
      @click="$emit('next')"
      :disabled="!hasNext"
      title="Next study"
    >
      <font-awesome-icon :icon="['fas', 'chevron-right']" />
    </button>

    <button
      v-if="showEditLabels"
      class="control-btn edit-labels-btn"
      @click="$emit('edit-labels')"
      title="Edit labels for this study"
    >
      <font-awesome-icon :icon="['fas', 'pen']" /><span class="btn-label">Edit Labels</span>
    </button>

    <button
      class="control-btn close-btn"
      @click="$emit('close')"
      title="Close this study"
    >
      <font-awesome-icon :icon="['fas', 'times']" /><span class="btn-label">Close</span>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  currentImageIndex: {
    type: Number,
    default: 0
  },
  totalImages: {
    type: Number,
    default: 0
  },
  showEditLabels: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['previous', 'next', 'close', 'edit-labels'])

const hasPrevious = computed(() => props.currentImageIndex > 0)
const hasNext = computed(() => props.currentImageIndex < props.totalImages - 1)
</script>

<style scoped>
.image-control-panel {
  position: absolute;
  top: 0.25rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.15rem 0.25rem;
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
  box-shadow: var(--shadow-standard);
  z-index: 1001;
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0.4rem;
  border: var(--border-width-standard) solid var(--color-divider-navy);
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.85rem;
  transform: translateZ(0);
}

.control-btn:hover:not(:disabled) {
  background-color: var(--color-bg-secondary);
  border-color: var(--color-border-primary);
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: var(--color-text-disabled);
}

.edit-labels-btn {
  background-color: var(--color-button-secondary);
  color: var(--color-text-primary);
}

.edit-labels-btn:hover {
  background-color: #5394d1;
  border-color: var(--color-border-primary);
}

.close-btn {
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
}

.close-btn:hover {
  background-color: var(--color-accent-hover) !important;
}

.btn-label {
  margin-left: 0.4rem;
  font-weight: var(--font-weight-semibold);
}

.image-counter {
  min-width: 60px;
  text-align: center;
  color: var(--color-text-primary);
  font-size: 0.9rem;
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .image-control-panel {
    top: calc(var(--header-height) + 0.5rem);
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
  }

  .control-btn {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }

  .image-counter {
    font-size: 0.8rem;
    min-width: 50px;
  }
}

.btn-label {
  margin-left: 0.25rem;
}
</style>